import React from "react";
import { Link } from "react-router-dom";

function PrivacyPolicy() {
  return (
    <div style={{ padding: "20px", lineHeight: 1.7, maxWidth: "960px", margin: "0 auto" }}>
      <h1>Privacy Policy</h1>
      <p>
        <strong>Last updated:</strong> October 6, 2026
      </p>
      <p>
        This policy explains what information is processed when you use VMCREATION,
        including website pages, games, tools, and chat features.
      </p>

      <h2>Cookies and similar technologies</h2>
      <p>
        We use cookies and browser storage (such as localStorage and IndexedDB) to run
        core features. Some storage is always active because it is required for the site
        to work correctly.
      </p>
      <ul>
        <li>
          <strong>Strictly necessary and functional storage:</strong> used for security,
          sign-in state, saved settings, and saved game or app progress.
        </li>
        <li>
          <strong>Optional analytics storage:</strong> used only if you opt in through
          the cookie banner or Cookie Settings.
        </li>
        <li>
          <strong>Optional marketing storage:</strong> used only if you opt in through
          the cookie banner or Cookie Settings.
        </li>
      </ul>
      <p>
        Optional categories are off by default unless you choose to enable them.
      </p>

      <h2>How to manage consent</h2>
      <p>
        You can accept, decline, or customize cookie choices in the consent banner.
        You can also revisit your choices at any time by opening Cookie Settings on the site.
      </p>

      <h2>What data is processed</h2>
      <ul>
        <li>Account data you provide (for example name, email, and profile fields).</li>
        <li>Chat metadata (for example participant IDs, timestamps, and conversation structure).</li>
        <li>Encrypted chat text and optional image uploads.</li>
        <li>Optional encrypted key-backup payloads if you choose backup or recovery.</li>
      </ul>

      <h2>Third-party services</h2>
      <p>
        This site uses Firebase services for authentication and database features.
        Those services may use their own technical storage needed for secure sessions and reliability.
      </p>

      <h2>Important honesty about security</h2>
      <ul>
        <li>Message text is encrypted client-side, but metadata is still visible to the backend.</li>
        <li>No system can guarantee perfect security; bugs, device compromise, or operator error can still cause exposure.</li>
        <li>If you lose your recovery passphrase, encrypted key backups may be unrecoverable.</li>
      </ul>

      <h2>AI features (QuizPopz and AI chatbot)</h2>
      <p>
        Some features send the text or files you enter (for example prompts, study notes, and
        uploaded material) to third-party AI providers such as OpenAI, Anthropic, or Google in
        order to generate quizzes, flashcards, or chatbot replies. Those providers process
        that content under their own terms and privacy policies, which we do not control.
      </p>
      <ul>
        <li>Do not enter personal, confidential, school, medical, financial, or other sensitive information into any AI feature.</li>
        <li>Do not enter any information about another person, especially a child.</li>
        <li>AI output can be wrong, biased, offensive, or misleading. It is provided for general information and entertainment only and is not professional, legal, medical, financial, or educational advice. Verify anything important independently.</li>
        <li>We do not use AI features to make decisions about you, and we do not knowingly sell your personal information.</li>
      </ul>

      <h2>Age restriction: 18 and older only</h2>
      <p>
        <strong>
          The AI features, chat, and account system on this website are intended only for people
          who are 18 years of age or older (or the age of majority where they live, if higher).
          They are not intended for, designed for, or directed at children or teenagers, and
          are not for use by school students or by anyone under 18.
        </strong>
      </p>
      <ul>
        <li>If you are under 18, you must not use the AI features, create an account, or send us any personal information. Please leave these features and ask a parent or guardian for advice.</li>
        <li>By using these features, you confirm that you are at least 18 years old and that you are not using them on behalf of a school, class, or minor.</li>
        <li>Teachers, schools, and parents must not require, direct, or allow students or children under 18 to use these features.</li>
        <li>We do not knowingly collect, use, or store personal information from anyone under 18. We do not offer parental controls or parental-consent flows because minors are not permitted users.</li>
        <li>If we learn that a person under 18 has used these features or provided personal information, we will delete the related account and data as soon as reasonably possible. If you believe a minor has used this service, contact us so we can remove the data.</li>
      </ul>
      <p>
        We may not be able to verify your age. You are responsible for making sure you meet the
        age requirement, and a parent or guardian is responsible for supervising any minor in
        their care and preventing their access.
      </p>

      <h2>Children's privacy</h2>
      <p>
        This website is not directed to children under 13 (COPPA, United States) or under 16
        (or the local age of digital consent, GDPR, EU/UK), and we do not knowingly collect
        their personal information. Non-AI pages, such as recipes and offline games, collect no
        account information from visitors.
      </p>

      <h2>Your rights and contact</h2>
      <p>
        Depending on where you live, you may have the right to access, correct, delete, or
        export your personal data, and to withdraw consent. To make a request, or to report a
        minor's use of this service, contact the site owner through the contact details
        published on this website. We will respond within a reasonable time as required by law.
      </p>

      <h2>Disclaimer and limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, this website, including all AI features, is
        provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind. The site owner is not
        liable for any direct, indirect, incidental, or consequential damages arising from your
        use of the service, from AI-generated content, from third-party AI providers, or from
        use of the service by anyone under the required age in breach of this policy. You agree
        to indemnify and hold the site owner harmless from claims arising from your violation of
        this policy, including any access by a minor under your control. Some jurisdictions do
        not allow certain limitations, so some of these terms may not apply to you.
      </p>

      <h2>Use-at-your-own-risk notice</h2>
      <p>
        Do not use this service to store or send highly sensitive personal, legal, financial, medical,
        or safety-critical information.
      </p>

      <p>
        By using this website, you acknowledge these limitations and accept responsibility for your usage choices.
        Please also review the <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
      </p>
    </div>
  );
}

export default PrivacyPolicy;
